import { ReviewResponse } from '../../../api/models/review-response';
import { ReviewSummaryResponse } from '../../../api/models/review-summary-response';
import { DEMO_APPS } from './apps.fixtures';
import { DEMO_PROFILES } from './app-profiles.fixtures';

/**
 * Review fixtures.
 *
 * Reviews are hand-written per app so the feed reads like real store feedback and reflects the
 * same storyline as the analytics and health fixtures (most notably the Acme Shopping checkout
 * crash in v4.12.0 and the praise that followed the 4.12.1 hotfix). Dates are expressed as
 * "days ago" and resolved at load time so the demo always looks current.
 */

type Store = 'GOOGLE_PLAY' | 'APP_STORE';
/** [daysAgo, store, rating, author, review text] */
type RawReview = [number, Store, number, string, string];

const G: Store = 'GOOGLE_PLAY';
const A: Store = 'APP_STORE';

// ------------------------------------------------------------------ Acme Shopping (8001)

const SHOPPING: RawReview[] = [
  [0.2, A, 5, 'Priya Raman', 'Apple Pay checkout takes about four seconds now. Ordered a coat on my commute and it arrived two days later. Smoothest shopping app I use.'],
  [0.6, G, 5, 'Marcus Delaney', 'Crashing is fixed! After the last update checkout works every time again. Thanks for turning it around so quickly.'],
  [1.1, G, 4, 'Holly Whitcombe', 'Back to being reliable after that rough patch. Would love a dark mode, my eyes are not happy at midnight browsing.'],
  [1.4, A, 5, 'Daniel Okafor', 'The wishlist price-drop alerts saved me $38 on a jacket I had been eyeing for weeks. Great feature.'],
  [2.0, G, 3, 'Sofia Lindqvist', 'Search is decent but filtering by size and colour together keeps resetting my choices. Checkout is fine again though.'],
  [2.6, A, 5, 'Theo Brandt', 'Returns are finally easy. Printed a label in the app, dropped the parcel off, refund landed in two days.'],
  [3.1, G, 4, 'Anika Shah', 'Fast and well designed. The order tracking map is lovely. Deducting a star because notifications sometimes arrive hours late.'],
  [3.8, G, 5, 'Liam Fitzgerald', 'Support chat sorted a missing item in under ten minutes and refunded me on the spot. Impressive.'],
  [4.3, A, 4, 'Camille Durand', 'Beautiful product pages and quick loading. I wish I could save multiple delivery addresses without re-entering them.'],
  [5.0, G, 5, 'Jasper Nguyen', 'Hotfix landed and everything works again. Bought two pairs of trainers with no hiccups. Five stars for the fast fix.'],
  [1.9, A, 2, 'Rebecca Holloway', 'Order confirmation screen took nearly a minute to load and I was not sure the payment had gone through. Emails confirmed it later, but that was stressful.'],
  [6.5, G, 4, 'Mateo Alvarez', 'Much better after the update. Loading used to hang at payment, now it is quick. Hope it stays that way.'],
  [7.2, A, 5, 'Ingrid Sørensen', 'Sized a dress using the fit guide and it was spot on. First time online clothes shopping has not needed a return.'],
  [8.0, G, 2, 'Darius Webb', 'Still seeing a crash on checkout on my older phone, even after updating. Support says a fix is rolling out so I will try again.'],
  [8.6, G, 1, 'Nadia Farouk', 'Checkout crashes every single time on my Pixel. I have tried five times today. Please fix, I am trying to buy a birthday gift.'],
  [9.2, G, 1, 'Connor Pike', 'Keeps crashing at the payment step. Uninstalled and reinstalled, same thing. Ended up buying from a competitor.'],
  [9.9, G, 2, 'Elena Petrova', 'Crashes when I tap Pay now. Cart is full of stuff I cannot buy. Used to love this app.'],
  [10.4, A, 4, 'Owen Gallagher', 'iPhone version works perfectly, but I can see lots of Android people complaining about crashes. Hope that gets fixed soon.'],
  [10.9, G, 1, 'Fatima Zahra', 'Since the update I cannot complete any order. App closes the moment the payment sheet opens. Android 12.'],
  [11.3, G, 1, 'Brandon Mills', 'Payment screen crashes every time. Tried on wifi and mobile data. Totally broken on my Galaxy.'],
  [11.8, G, 1, 'Sienna Castellano', 'Crash at checkout, and I was charged twice when I tried again from the website after the app failed. Waiting on a refund.'],
  [12.2, G, 2, 'Hiroshi Tanaka', 'The new one-tap payment looks great but it crashes my phone every time. Please roll back to the old version.'],
  [12.5, G, 1, 'Megan O\'Brien', 'App crashes on launch of the payment page. I have tried clearing cache and reinstalling. Nothing works.'],
  [12.9, G, 1, 'Aaron Whitfield', 'Crashing constantly since the new version came out. Cannot check out at all.'],
  [13.4, G, 2, 'Lucia Romano', 'After updating to the latest version the app force-closes at checkout. Was fine last week.'],
  [13.8, A, 5, 'Gabriel Martins', 'Love the new home screen, recommendations finally match what I actually buy. Fast on iPhone 15.'],
  [14.6, G, 4, 'Hannah Kowalski', 'Good prices and quick delivery. Sometimes the app is slow to open after a few days unused, but otherwise very solid.'],
  [15.9, A, 5, 'Victor Ramos', 'Autumn Sale was a blast. Got the coupon applied automatically at checkout, no code hunting. More of this please.'],
  [16.8, G, 4, 'Zoe Halloran', 'Clean design, easy to find deals. A barcode scanner for in-store price checks would make this perfect.'],
  [18.2, A, 3, 'Patrick Lowe', 'Fine for regular shopping, but the delivery-slot picker is confusing. Took me three tries to book an evening slot.'],
  [19.4, G, 5, 'Amira Hassan', 'Used the sale to stock up on school supplies. App handled a huge cart with no slowdowns.'],
  [20.3, A, 5, 'Kieran Doyle', 'Excellent. The size-guide feature and free returns make it my default clothing store now.'],
  [21.1, G, 4, 'Mei Chen', 'Sale prices were great. A couple of items showed in stock then sold out at checkout, which was annoying.'],
  [22.5, A, 2, 'Oliver Grant', 'Item was listed as arriving Tuesday and turned up Friday. No proactive update from the app. Tracking needs to be honest.'],
  [24.0, G, 5, 'Isabel Moreno', 'Really happy with how easy reordering is. Two taps to rebuy my usual pantry items.'],
  [25.3, A, 4, 'Noah Bergström', 'Quick, tidy, and gift-wrapping options are a nice touch. Would like Apple Watch order notifications.'],
  [26.8, G, 3, 'Chloe Adeyemi', 'Does the job. Loyalty points are not showing in my cart summary, so I cannot tell what I will earn.'],
  [28.1, A, 5, 'Rafael Costa', 'Switched from a competitor and have not looked back. Delivery tracking is accurate to the hour.'],
];

// ------------------------------------------------------------------ Acme Loyalty (8002)

const LOYALTY: RawReview[] = [
  [0.4, G, 5, 'Beth Hargrove', 'Scan at the till, points show up instantly, done. Redeemed a free coffee this morning without any fuss.'],
  [1.2, G, 5, 'Jonathan Reyes', 'Double-points weekend was brilliant. Earned enough for a free grocery voucher in two trips.'],
  [2.1, G, 4, 'Adaeze Nwosu', 'Love the tier progress bar. Wish I could see expiry dates for points directly on the home screen.'],
  [3.0, G, 5, 'Tom Whitaker', 'Most useful app on my phone. The digital card never fails, even when my signal is terrible in the car park.'],
  [3.9, G, 3, 'Kavita Menon', 'Works well overall, but the widget on my home screen shows an outdated points balance until I open the app.'],
  [4.7, G, 5, 'Sean Kavanagh', 'Offers are actually relevant to what I buy. Saved $22 this week using the targeted coupons.'],
  [5.5, G, 4, 'Lena Fischer', 'Smooth and quick. Would like to be able to share my points with my partner as a household account.'],
  [6.3, G, 5, 'Grace Ibarra', 'Customer support sorted a missing points credit from a receipt within a day. Friendly and thorough.'],
  [7.4, G, 2, 'Roy Castellanos', 'Points from my online order did not appear for five days. I had to contact support before they were credited.'],
  [8.8, G, 5, 'Emily Tran', 'The birthday reward was a lovely surprise. Redeemed it in the app in seconds.'],
  [9.6, G, 4, 'Mohamed Saleh', 'Reliable and simple. The QR card occasionally needs two attempts to scan if brightness is low.'],
  [11.0, G, 5, 'Alicia Barrett', 'I have been a member for three years. This app keeps getting better. Love the new offers tab.'],
  [12.3, G, 4, 'Patrick Sullivan', 'Good rewards, easy to use. A dark theme would be nice for evening browsing.'],
  [13.7, G, 3, 'Jia Wei', 'Push notifications for offers arrive after the offer expires on my phone. Timing needs fixing.'],
  [15.2, G, 5, 'Ruth Abernathy', 'Finally, a loyalty app that does not spam me. Notifications are limited to offers I actually use.'],
  [17.0, G, 5, 'Diego Herrera', 'Fast, clean, and my grandparents can use it. The large-text mode is a thoughtful touch.'],
  [18.8, G, 4, 'Naomi Schwartz', 'Great value. Adding a map of participating stores with opening hours would be the cherry on top.'],
  [20.4, G, 1, 'Wesley Brantley', 'Locked out after the password reset email never arrived. Waited two days for support to respond.'],
  [22.1, G, 5, 'Sophie Laurent', 'The tier upgrade came with early access to sales. Already paying for itself.'],
  [24.0, G, 4, 'Anthony Rossi', 'Simple and effective. Would like a spend summary by month so I can see where points come from.'],
  [26.2, G, 5, 'Kendra Ofori', 'Used the app at three different stores this week. Worked everywhere. Thanks!'],
  [27.9, G, 3, 'Felix Andersson', 'Does what it says. The home screen shows too many banner ads compared to my actual rewards.'],
];

// ------------------------------------------------------------------ Acme Delivery Partner (8003)

const DELIVERY: RawReview[] = [
  [0.3, A, 4, 'Jerome Whitfield', 'Earning tracker makes it easy to see how close I am to my weekly goal. Payouts hit my account the next morning.'],
  [0.9, A, 2, 'Andre Kowalczyk', 'Order list freezes for a few seconds every time a new order comes in, especially during the dinner rush. Cost me an order.'],
  [1.6, A, 3, 'Tasha Greene', 'Good app overall. GPS drifts on my route and keeps showing me a block behind where I actually am.'],
  [2.2, A, 5, 'Luis Fernandez', 'Best courier app I have worked with. Clear pickup instructions and the in-app chat with support is fast.'],
  [3.0, A, 1, 'Dwayne Holloway', 'App crashed mid-delivery and I lost my active order. Had to call dispatch to get it reassigned. Happened twice this week.'],
  [3.7, A, 2, 'Priyanka Nair', 'Battery drops about 40% in a four-hour shift. Having the location running all the time is draining my iPhone 11.'],
  [4.5, A, 4, 'Terrence Boyd', 'Love the new heat map showing where orders are busy. Saves me from driving around looking for work.'],
  [5.2, A, 5, 'Hannah Le', 'The navigation hand-off to Apple Maps works perfectly and proof-of-delivery photos upload instantly.'],
  [6.1, A, 3, 'Miguel Santos', 'Would be great to see the tip amount before accepting an order. Right now I find out after delivery.'],
  [7.0, A, 4, 'Olivia Brandt', 'Solid upgrade. The order batching feature is helping me finish more deliveries per hour.'],
  [8.3, A, 1, 'Calvin Dawson', 'App froze on the assignment screen for almost a minute during the dinner rush. Missed three orders and my rating dropped.'],
  [9.4, A, 5, 'Samira Khan', 'Payouts are fast, the support team actually answers, and the new earnings breakdown is helpful.'],
  [10.8, A, 3, 'Jordan Mitchell', 'Fine most of the time. The map sometimes fails to load on cellular, then I have to restart the app.'],
  [12.1, A, 4, 'Victoria Adler', 'Onboarding took ten minutes and I was delivering the same day. Would like an option to pause orders for a break.'],
  [13.5, A, 2, 'Kwame Asante', 'Notifications for new orders arrive late if the phone is locked. I have lost good orders to other couriers because of it.'],
  [15.0, A, 5, 'Elise Moreau', 'Great tool for part-time work. I like that I can see the delivery address before accepting.'],
  [16.4, A, 4, 'Nathan Brooks', 'Reliable for the most part. Could use a clearer way to report a closed restaurant.'],
  [18.0, A, 3, 'Imani Clarke', 'Decent app, but it logs me out randomly a few times a week. Sign in is slow.'],
  [19.6, A, 5, 'Stefan Novak', 'Very clear earnings summary, easy instant payout. This is what a courier app should be.'],
  [21.3, A, 2, 'Ricardo Mendes', 'Wrong pin drop for a customer address sent me to the wrong building twice this week. Need a way to confirm the location.'],
  [23.0, A, 4, 'Bianca Rossi', 'Good support and quick payouts. App occasionally shows last week\'s order history on the home screen.'],
  [25.5, A, 5, 'Deshawn Carter', 'Been with Acme a few months. Orders are steady and the app is straightforward.'],
  [27.2, A, 3, 'Maya Patel', 'Would like more predictable order assignments. Sometimes two orders are offered from opposite ends of town.'],
];

// ------------------------------------------------------------------ Acme Rewards Beta (8004)

const BETA: RawReview[] = [
  [9.5, G, 2, 'Beta Tester 4117', 'Crashes when opening the rewards catalogue on Android 13. Reported through the feedback form too.'],
  [11.3, G, 4, 'Naveen Pillai', 'Really promising beta. Love the new streak bonus concept. A few rough edges but clearly early days.'],
  [13.0, G, 3, 'Clara Hewitt', 'Redeem flow is confusing. I could not tell which rewards I could afford with my current points.'],
  [15.4, G, 1, 'Miles Donovan', 'Would not load at all after the last beta update. Stuck on the splash screen.'],
  [18.9, G, 5, 'Yuki Nakamura', 'Fun, fast and the streak animations are delightful. Count me in for the full release.'],
  [21.7, G, 3, 'Adebayo Okon', 'Good start. Missing a way to transfer points from the main Loyalty app.'],
  [24.2, G, 4, 'Lauren Whitby', 'Happy to be testing. Interface feels fresher than the main app. Hope the catalogue gets more items.'],
  [27.1, G, 2, 'Gareth Pugh', 'Battery drain is significant. The app is waking my phone every few minutes in the background.'],
];

// ------------------------------------------------------------------ assembly

const RAW_BY_APP: Record<number, RawReview[]> = { 8001: SHOPPING, 8002: LOYALTY, 8003: DELIVERY, 8004: BETA };

const daysAgoIso = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

/** Deterministic score inside a rating's band, so scores look organic but never change between loads. */
function sentimentFor(rating: number, salt: number): { sentiment: ReviewResponse['sentiment']; score: number } {
  const jitter = ((salt * 37) % 100) / 100; // 0..0.99
  if (rating >= 4) {
    const base = rating === 5 ? 0.78 : 0.52;
    return { sentiment: 'POSITIVE', score: Math.round((base + jitter * 0.18) * 100) / 100 };
  }
  if (rating === 3) {
    return { sentiment: 'NEUTRAL', score: Math.round((-0.12 + jitter * 0.3) * 100) / 100 };
  }
  const base = rating === 2 ? -0.35 : -0.72;
  return { sentiment: 'NEGATIVE', score: Math.round((base - jitter * 0.2) * 100) / 100 };
}

let nextReviewId = 12001;

function buildReviews(): ReviewResponse[] {
  return DEMO_APPS.flatMap((app) =>
    (RAW_BY_APP[app.id!] ?? []).map(([daysAgo, platform, rating, userName, content]) => {
      const id = nextReviewId++;
      const { sentiment, score } = sentimentFor(rating, id);
      return {
        id,
        reviewId: `demo-review-${id}`,
        appId: app.id,
        appName: app.name,
        platform,
        rating,
        sentiment,
        sentimentScore: score,
        content,
        userName,
        reviewDate: daysAgoIso(daysAgo),
        createdAt: daysAgoIso(daysAgo),
        updatedAt: daysAgoIso(daysAgo),
      } satisfies ReviewResponse;
    })
  );
}

export const DEMO_REVIEWS: ReviewResponse[] = buildReviews().sort(
  (a, b) => new Date(b.reviewDate ?? 0).getTime() - new Date(a.reviewDate ?? 0).getTime()
);

// ------------------------------------------------------------------ review summary

const pct = (part: number, total: number) => (total ? Math.round((part / total) * 1000) / 10 : 0);

/**
 * Review Summary page data. Lifetime store totals come from the app profile (a real app has
 * thousands of reviews; the list above is the recent feed), so the two views complement each other.
 */
export function buildReviewSummary(appId: number): ReviewSummaryResponse | undefined {
  const profile = DEMO_PROFILES[appId];
  const app = DEMO_APPS.find((a) => a.id === appId);
  if (!profile || !app) {
    return undefined;
  }

  const [one, two, three, four, five] = profile.reviewSummary.starCounts;
  const total = one + two + three + four + five;
  const averageRating = Math.round(((one + two * 2 + three * 3 + four * 4 + five * 5) / total) * 10) / 10;
  const { positive, neutral, negative } = profile.reviewSummary.sentiment;

  return {
    appId,
    appName: app.name,
    totalReviews: total,
    averageRating,
    ratingDistribution: {
      oneStar: one,
      oneStarPercentage: pct(one, total),
      twoStar: two,
      twoStarPercentage: pct(two, total),
      threeStar: three,
      threeStarPercentage: pct(three, total),
      fourStar: four,
      fourStarPercentage: pct(four, total),
      fiveStar: five,
      fiveStarPercentage: pct(five, total),
    },
    sentimentDistribution: {
      positive,
      positivePercentage: pct(positive, total),
      neutral,
      neutralPercentage: pct(neutral, total),
      negative,
      negativePercentage: pct(negative, total),
    },
    platformSummaries: Object.fromEntries(
      profile.reviewSummary.platforms.map((p) => [
        p.platform,
        { platform: p.platform, averageRating: p.averageRating, totalReviews: p.totalReviews },
      ])
    ),
  };
}
